/**
 * extensions/dicom-rt/src/modules/dicom-measurement/src/suv/computeAllRoiSuvAcrossTimepoints.js
 *
 * E-1: bridge entries now include suvHistogram + petVoxelVolumeMm3.
 *
 * petVoxelVolumeMm3 is computed ONCE per timepoint from the PET imagePlane
 * metadata (not per-ROI, since all ROIs share the same PET voxel grid).
 * It is stamped onto every bridge entry for that timepoint so that
 * computePsmaPosVolumesFromBridge can convert voxel counts to mm³ without
 * needing to re-read metadata.
 *
 * petVoxelVolumeMm3 derivation:
 *   in-plane area = columnPixelSpacing × rowPixelSpacing (mm²)
 *   slice spacing: try imagePlaneModule.sliceThickness first; if zero/absent,
 *                  compute from 3D distance between first two slice positions.
 *   petVoxelVolumeMm3 = in-plane area × slice spacing
 *
 * Bridge entry shape (after E-1):
 * {
 *   suvMax, suvMean, suvMin, suvSD,    ← flat (backward compat)
 *   voxelCount, factors,
 *   suv: { suvMax, suvMean, suvMin, suvSD },  ← nested (liver formula reads this)
 *   suvHistogram: {
 *     binWidth: 0.05, binCount: 400,
 *     bins: Int32Array(400),  ← bins[i] = count with SUV ∈ [i×0.05, (i+1)×0.05)
 *     overflow: number        ← count with SUV ≥ 20
 *   },
 *   petVoxelVolumeMm3: number  ← mm³ per PET voxel
 * }
 *
 * Console verification (§E-1 verify):
 *   window.__SUV_BRIDGE_DUMP__()  → then inspect any entry:
 *   const e = window.__SUV_BRIDGE_V10__.data[key]
 *   const histSum = e.suvHistogram.bins.reduce((a,b)=>a+b,0) + e.suvHistogram.overflow
 *   console.log('hist sum === voxelCount?', histSum, e.voxelCount)
 *   console.log('petVoxelVolumeMm3:', e.petVoxelVolumeMm3)
 *   const meanFromHist = ... (manual bin-weighted average, should ≈ suvMean)
 */

import cornerstone from 'cornerstone-core';
import cornerstoneTools from 'cornerstone-tools';

import {
  computeRoiSuv,
  buildCtToPetImageIdMap,
} from '../../../../../../cornerstone/src/modules/suv/computeRoiSuv';
import {
  makeSuvKey,
  setSuvByKey,
  startComputing,
  stopComputing,
} from './suvViewportBridge';

const log = (...args) => {
  // eslint-disable-next-line no-console
  console.log('[SUV-COMPUTE]', ...args);
};

/**
 * Compute PET voxel volume (mm³) for a timepoint.
 * Uses the ordered petImageIds array — computing from adjacent positions is
 * more reliable than sliceThickness (which may be 0 or absent for some PET
 * series).
 *
 * @param {string[]} petImageIds  ordered array of PET imageIds for this tp
 * @returns {number|null}
 */
export function computePetVoxelVolumeMm3(petImageIds) {
  if (!petImageIds.length) return null;
  const meta0 = cornerstone.metaData.get('imagePlaneModule', petImageIds[0]);
  if (!meta0) return null;

  const cs = meta0.columnPixelSpacing;
  const rs = meta0.rowPixelSpacing;
  if (!cs || !rs || cs <= 0 || rs <= 0) return null;
  const inPlaneMm2 = cs * rs;

  // Slice spacing: prefer sliceThickness; fall back to inter-slice distance.
  let sliceSpacingMm = meta0.sliceThickness || 0;
  if (sliceSpacingMm <= 0 && petImageIds.length > 1) {
    const meta1 = cornerstone.metaData.get('imagePlaneModule', petImageIds[1]);
    if (meta1?.imagePositionPatient && meta0?.imagePositionPatient) {
      const ipp0 = meta0.imagePositionPatient;
      const ipp1 = meta1.imagePositionPatient;
      const dx = ipp1[0] - ipp0[0];
      const dy = ipp1[1] - ipp0[1];
      const dz = ipp1[2] - ipp0[2];
      sliceSpacingMm = Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
  }

  if (sliceSpacingMm <= 0) {
    // eslint-disable-next-line no-console
    console.warn(
      '[SUV-COMPUTE] Could not determine PET slice spacing — psmaPosVolume will be null'
    );
    return null;
  }
  return inPlaneMm2 * sliceSpacingMm;
}

export function extractContoursByCtSlice(
  structureSetUID,
  roiNumber,
  ctImageIds
) {
  const { globalImageIdSpecificToolStateManager } = cornerstoneTools;
  const all = globalImageIdSpecificToolStateManager.saveToolState();
  const out = [];
  for (const ctImageId of ctImageIds) {
    const slice = all[ctImageId];
    if (!slice) continue;
    const rt = slice.RTStructDisplayTool;
    if (!rt || !Array.isArray(rt.data)) continue;
    const matches = rt.data.filter(
      d =>
        d.structureSetSeriesInstanceUid === structureSetUID &&
        d.ROINumber === roiNumber
    );
    for (const m of matches) {
      const pts = m.handles?.points;
      if (!pts || pts.length < 3) continue;
      const points = pts
        .map(p => (p && p.x != null && p.y != null ? { x: p.x, y: p.y } : null))
        .filter(Boolean);
      if (points.length >= 3) out.push({ ctImageId, points });
    }
  }
  return out;
}

export function discoverTimepointPairs(imageSets) {
  const pairs = [];
  for (const ctSet of imageSets) {
    if (!ctSet) continue;
    const structureSetUID = ctSet.rtstructSeriesInstanceUID;
    if (!structureSetUID) continue;
    if (!ctSet.isFusion || !ctSet.referencedDisplaySet) continue;
    const ctImages = ctSet.images;
    const petImages = ctSet.referencedDisplaySet.images;
    if (!ctImages?.length || !petImages?.length) continue;
    pairs.push({
      structureSetUID,
      ctSeriesUID: ctSet.SeriesInstanceUID,
      ctImageIds: ctImages.map(img => img.getImageId()),
      petImageIds: petImages.map(img => img.getImageId()),
    });
  }
  return pairs;
}

/**
 * Build a complete bridge entry from computeRoiSuv result.
 * Writes flat SUV fields (backward compat), nested suv: {} sub-object
 * (for liver threshold formula), histogram, and petVoxelVolumeMm3.
 */
export function makeBridgeEntry(result) {
  const {
    suvMax,
    suvMean,
    suvMin,
    suvSD,
    voxelCount,
    factors,
    suvHistogram,
    petVoxelVolumeMm3,
  } = result;
  return {
    // Flat fields — backward compat (MeasurementSection Row)
    suvMax: suvMax ?? null,
    suvMean: suvMean ?? null,
    suvMin: suvMin ?? null,
    suvSD: suvSD ?? null,
    voxelCount,
    factors,
    // Nested sub-object — used by computeRecipThresholds (liver formula)
    suv: {
      suvMax: suvMax ?? null,
      suvMean: suvMean ?? null,
      suvMin: suvMin ?? null,
      suvSD: suvSD ?? null,
    },
    // NEW E-1: histogram + voxel volume (for PSMA+ volume computation)
    suvHistogram: suvHistogram ?? null,
    petVoxelVolumeMm3: petVoxelVolumeMm3 ?? null,
  };
}

export function makeEmptyEntry(reason, petVoxelVolumeMm3) {
  return {
    suvMax: null,
    suvMean: null,
    suvMin: null,
    suvSD: null,
    voxelCount: 0,
    factors: { ok: false, reason },
    suv: { suvMax: null, suvMean: null, suvMin: null, suvSD: null },
    suvHistogram: null,
    petVoxelVolumeMm3: petVoxelVolumeMm3 ?? null,
  };
}

export default async function computeAllRoiSuvAcrossTimepoints({
  imageSets,
  getStructureSet,
}) {
  const pairs = discoverTimepointPairs(imageSets);
  log(
    'start — pairs:',
    pairs.length,
    pairs.map(
      p => `ct:${p.ctSeriesUID.slice(-8)}↔ss:${p.structureSetUID.slice(-8)}`
    )
  );
  if (!pairs.length) {
    log('no fused CT-PET pairs — nothing to compute');
    return;
  }

  startComputing();
  let totalRoisProcessed = 0;
  try {
    for (const {
      structureSetUID,
      ctImageIds,
      petImageIds,
      ctSeriesUID,
    } of pairs) {
      const ss = getStructureSet(structureSetUID);
      const rois = ss?.ROIContours || [];
      if (!rois.length) continue;

      // Compute PET voxel volume once per timepoint (same for all ROIs)
      const petVoxelVolumeMm3 = computePetVoxelVolumeMm3(petImageIds);
      log(
        `   timepoint ct:${ctSeriesUID.slice(-8)} — ${
          rois.length
        } ROIs, voxelVol=${petVoxelVolumeMm3?.toFixed(4) ?? 'null'} mm³`
      );

      const ctToPetMap = buildCtToPetImageIdMap(ctImageIds, petImageIds);

      // Parallel preload: extract all contours upfront (cheap toolState reads),
      // collect the union of PET imageIds needed, then fetch them in batches of 8.
      // After preload, every loadAndCacheImage call inside computeRoiSuv hits the
      // cache and returns synchronously — serial await cost becomes negligible.
      const contoursPerRoi = new Map();
      const neededPetIds = new Set();
      for (const roi of rois) {
        const contoursByCtSlice = extractContoursByCtSlice(
          structureSetUID,
          roi.ROINumber,
          ctImageIds
        );
        contoursPerRoi.set(roi.ROINumber, contoursByCtSlice);
        for (const { ctImageId } of contoursByCtSlice) {
          const petId = ctToPetMap.get(ctImageId);
          if (petId) neededPetIds.add(petId);
        }
      }
      const petIdsArray = [...neededPetIds];
      log(`   preloading ${petIdsArray.length} PET slices for this timepoint`);
      const PRELOAD_BATCH = 8;
      for (let i = 0; i < petIdsArray.length; i += PRELOAD_BATCH) {
        const batch = petIdsArray.slice(i, i + PRELOAD_BATCH);
        await Promise.all(
          batch.map(id =>
            cornerstone.loadAndCacheImage(id).catch(err => {
              // eslint-disable-next-line no-console
              console.warn('[SUV-COMPUTE] preload failed', id.slice(-30), err);
              return null; // don't fail the whole batch on one bad slice
            })
          )
        );
      }

      for (const roi of rois) {
        const key = makeSuvKey(structureSetUID, roi.ROINumber, ctSeriesUID);
        const contoursByCtSlice = contoursPerRoi.get(roi.ROINumber);
        if (!contoursByCtSlice.length) {
          setSuvByKey(
            key,
            makeEmptyEntry('no-contours-on-fused-ct', petVoxelVolumeMm3)
          );
          totalRoisProcessed++;
          continue;
        }
        try {
          const result = await computeRoiSuv({
            contoursByCtSlice,
            ctToPetImageIdMap: ctToPetMap,
            petVoxelVolumeMm3,
          });
          setSuvByKey(key, makeBridgeEntry(result));
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[SUV-COMPUTE] ROI failed', { key, err });
          setSuvByKey(key, makeEmptyEntry('compute-error', petVoxelVolumeMm3));
        }
        totalRoisProcessed++;
      }
    }
    log('done — total ROIs processed:', totalRoisProcessed);
  } finally {
    stopComputing();
  }
}
